import { formatDate } from '@angular/common';
import { Component, ElementRef, inject, signal, viewChildren } from '@angular/core';
import { MatTooltip } from '@angular/material/tooltip';
import { TemplateType } from '@app/shared/classes/template-type';
import { downloadFile } from '@app/shared/download-utils';
import { BackendService } from '@app/shared/services/backend.service';
import { type Sprache, sprachen } from '@app/shared/sprache';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { rxResource } from "@angular/core/rxjs-interop";

@Component({
  selector: 'app-admin-stammdaten-template',
  templateUrl: './admin-stammdaten-template.component.html',
  imports: [
    MatTooltip,
    TranslatePipe,
  ],
})
export class AdminStammdatenTemplateComponent {
  private readonly backendService = inject(BackendService);
  private readonly translateService = inject(TranslateService);
  private readonly fileInputs = viewChildren<ElementRef>('fileInput');

  protected readonly templateList = rxResource({ stream: () => this.backendService.listTemplateTypes() })

  protected readonly sprachen = sprachen;
  protected readonly uploadSuccess = signal('');
  protected readonly uploadError = signal('');
  protected readonly downloadError = signal('');

  upload(type: TemplateType, sprache: Sprache, event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    this.uploadSuccess.set('');
    this.uploadError.set('');
    this.downloadError.set('');
    if (file) {
      this.backendService.uploadTemplate(type, sprache, file)
        .subscribe({
          next: () => {
            this.uploadSuccess.set(this.translateService.instant(type.messageProperty) + ' ' + sprache);
            this.templateList.reload();
          },
          error: () => this.uploadError.set(this.translateService.instant(type.messageProperty) + ' ' + sprache)
        });
    } else {
      this.uploadError.set(this.translateService.instant(type.messageProperty) + ' ' + sprache);
    }
    this.fileInputs().forEach( it => it.nativeElement.value = null );
  }

  download(type: TemplateType, sprache: Sprache) {
    this.uploadSuccess.set('');
    this.uploadError.set('');
    this.downloadError.set('');
    this.backendService.downloadTemplate(type, sprache)
      .subscribe({
        next: downloadFile,
        error: error => {
          console.error(error);
          this.downloadError.set(this.translateService.instant(type.messageProperty) + ' ' + sprache);
        }
      });
  }

  downloadTooltip(type: TemplateType, sprache: Sprache): string {
    let tooltip = this.translateService.instant('admin.template.download.tooltip');
    let changed = this.lastChanged(type, sprache);
    if (changed) {
      tooltip += '\n(' + formatDate(changed, 'dd.MM.yyyy HH:mm', 'de-CH') + ')'
    }
    return tooltip;
  }

  downloadDummy() {
    this.uploadSuccess.set('');
    this.uploadError.set('');
    this.downloadError.set('');
    this.backendService.downloadDummyTemplate()
      .subscribe({
        next: downloadFile,
        error: error => {
          console.error(error);
          this.downloadError.set(this.translateService.instant('admin.template.dummy.label'));
        }
      });
  }

  lastChanged(type: TemplateType, sprache: Sprache): string | null {
    return this.templateList.value()?.changed?.[sprache]?.[type.id] ?? null;
  }
}
